<script setup lang="ts">
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import DeliveryPlanSearch from "@/components/DailyReportEdit/Combine/DeliveryPlanSearch.vue";
import DeliveryPlanList from "@/components/DailyReportEdit/Combine/DeliveryPlanList.vue";
import getDailyReportResponse from "@/types/responses/getDailyReport";
import getDailyReportRequest from "@/types/requests/getDailyReport";
import { ref, reactive, computed, onMounted } from 'vue';
import Api from "@/services/api/ApiServiceFabrick";
import { formatDate } from "@/services/functions";
import DeliveryPlan from "@/types/DeliveryPlan";

const emit = defineEmits(['update:modelValue']);

const INITIAL_RANGE_DAYS = 3;
const deliveryPlans = ref<DeliveryPlan[]>([]);
const totalPage = ref<number>(0);
const page = ref<number>(1);
const isSearching = ref<boolean>(false);

const props = defineProps<{
  modelValue: (DeliveryPlan | getDeliveryPlanVehicleInformation)[]
  deliveryPlan: getDeliveryPlanVehicleInformation
}>();

function getInitialDateRange(leavingDateTime: string): Date[] {
  const startDate = new Date(leavingDateTime);
  const endDate = new Date(leavingDateTime);
  startDate.setDate(startDate.getDate() - INITIAL_RANGE_DAYS);
  endDate.setDate(endDate.getDate() + INITIAL_RANGE_DAYS);

  return [startDate, endDate];
}

const searchParams = reactive({
  operation_office_id: props.deliveryPlan.driver.officeable_id,
  driver_id: props.deliveryPlan.driver_id,
  account_id: props.deliveryPlan.driver.id,
  daterange: getInitialDateRange(props.deliveryPlan.leaving_warehouse_date_time),
  selectDateType: 'leaving',
})

onMounted(() => {
  getDeliveryPlans(makeInitialParams());
});

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

function getDeliveryPlans(req: getDailyReportRequest) {
  isSearching.value = true;
  Api().getDailyReports(req).then((data: getDailyReportResponse) => {
    deliveryPlans.value = data.items;
    totalPage.value = data.total_page;
    page.value = data.current_page;
  }).finally(() => {
    isSearching.value = false;
  });
}

function makeInitialParams(): getDailyReportRequest {
  searchParams.selectDateType
  return {
    operation_office_id: searchParams.operation_office_id,
    driver_id: searchParams.driver_id,
    account_id: searchParams.account_id,
    daterange: convertDateTimeToDate(searchParams.daterange[0]) + " - " + convertDateTimeToDate(searchParams.daterange[1]),
    delivery_date: 1,
    return_date: 0,
    page: page.value
  }
}

function makeParams(): getDailyReportRequest {
  return {
    operation_office_id: searchParams.operation_office_id,
    driver_id: searchParams.driver_id,
    account_id: searchParams.account_id,
    daterange: convertDateTimeToDate(searchParams.daterange[0]) + " - " + convertDateTimeToDate(searchParams.daterange[1]),
    delivery_date: searchParams.selectDateType === 'leaving' ? 1 : 0,
    return_date: searchParams.selectDateType === 'returning' ? 1 : 0,
    page: page.value
  }
}

function convertDateTimeToDate(datetime: Date | undefined): string {
  if (!datetime) {
    return ''
  }
  return formatDate(datetime, '/')
}

function updatePage(newPage: number) {
  page.value = newPage;
  getDeliveryPlans(makeParams());
}

</script>
<template>
  <v-card
    class="w-100"
  >
    <DeliveryPlanSearch
      :deliveryPlan="props.deliveryPlan"
      v-model="searchParams"
      :isSearching="isSearching"
      @search="getDeliveryPlans(makeParams())"
    />
    <DeliveryPlanList
      v-model="modelValue"
      :deliveryPlans="deliveryPlans"
      :page="page"
      :totalPage="totalPage"
      @updatePage="updatePage"
    />
  </v-card>
</template>
